import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Palette,
  Video,
  TrendingUp,
  Globe,
  CheckCircle,
  Star,
  Users,
  Award,
  Zap,
  Target,
  BarChart3,
  Play
} from 'lucide-react';

// Animated Counter
const AnimatedCounter = ({ end, suffix = '', duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = React.useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Scroll Section with Animation
const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
};

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.5], ['0%', '10%']);

  const services = [
    {
      icon: Palette,
      title: 'Branding & Design',
      description: 'Strategic brand identity, visual systems, and creative assets that make you unforgettable.',
    },
    {
      icon: Video,
      title: 'Video Production',
      description: 'Cinematic content from concept to delivery — brand films, ads, and social media that converts.',
    },
    {
      icon: TrendingUp,
      title: 'Performance Marketing',
      description: 'Data-driven campaigns across paid social, search, and display that deliver measurable ROI.',
    },
    {
      icon: Globe,
      title: 'Website & Tech Solutions',
      description: 'Modern, conversion-optimized websites and digital platforms built for growth.',
    },
  ];

  const whyChooseUs = [
    {
      icon: Target,
      title: 'Strategy-First Approach',
      description: 'We start with deep research and clear objectives before creating anything.',
    },
    {
      icon: BarChart3,
      title: 'Measurable Results',
      description: 'Every campaign is tracked, analyzed, and optimized for maximum impact.',
    },
    {
      icon: Zap,
      title: 'End-to-End Execution',
      description: 'From creative to delivery — one team, no handoffs, no gaps.',
    },
    {
      icon: Award,
      title: '15+ Years Experience',
      description: 'Proven expertise serving education, startups, and enterprise clients.',
    },
  ];

  const stats = [
    { value: 15, suffix: '+', label: 'Years Experience' },
    { value: 500, suffix: '+', label: 'Projects Delivered' },
    { value: 200, suffix: '+', label: 'Happy Clients' },
    { value: 98, suffix: '%', label: 'Client Retention' },
  ];

  const testimonials = [
    {
      quote: "Focus Pixel transformed our digital presence completely. Our lead generation increased by 300% within 6 months.",
      author: "Dr. Ramesh Gupta",
      role: "Director, Excellence Coaching",
      avatar: "RG",
    },
    {
      quote: "The video content they produced elevated our brand perception. Professional, creative, and always on time.",
      author: "Kavita Mehta",
      role: "Founder, Urban Fitness Studio",
      avatar: "KM",
    },
    {
      quote: "Their strategic approach to marketing helped us scale from a local institute to a recognized brand.",
      author: "Vikram Singh",
      role: "CEO, TechLearn Academy",
      avatar: "VS",
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center hero-gradient">
        <motion.div
          className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20"
          style={{ opacity: heroOpacity, y: heroY }}
        >
          <div className="max-w-4xl">
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-primary text-sm font-medium">Digital Growth Partner</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              We Build Digital Growth Engines for{' '}
              <span className="text-gradient">Ambitious Brands.</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="text-lg md:text-xl text-fp-textMuted max-w-2xl mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              From strategy to execution — branding, video production, and performance 
              marketing that delivers measurable results.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/book-call"
                className="btn-primary flex items-center justify-center gap-2 text-base"
                data-testid="hero-cta-primary"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/portfolio"
                className="btn-secondary flex items-center justify-center gap-2 text-base"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-4 h-4" />
                View Our Work
              </Link>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              className="flex items-center gap-6 mt-12 pt-8 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex -space-x-3">
                {['RG', 'KM', 'VS', 'AP'].map((initials, i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-semibold border-2 border-fp-bg"
                  >
                    {initials}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-fp-textMuted text-sm mt-1">Trusted by 200+ brands</p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
            <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
              <motion.div
                className="w-1.5 h-1.5 rounded-full bg-primary"
                animate={{ y: [0, 12, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-fp-surface/50 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScrollSection key={stat.label} delay={index * 0.1}>
                <div className="text-center">
                  <div className="stat-number mb-2">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-fp-textMuted text-sm">{stat.label}</p>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollSection>
              <div>
                <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">About Us</p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                  Your Strategic Partner for Digital Growth
                </h2>
                <p className="text-fp-textMuted text-lg leading-relaxed mb-6">
                  Since 2008, Focus Pixel has been helping ambitious brands scale through 
                  strategic creative and digital marketing. We combine deep industry expertise 
                  with cutting-edge execution to deliver results that matter.
                </p>
                <p className="text-fp-textMuted leading-relaxed mb-8">
                  From educational institutions to tech startups, we've partnered with over 
                  200 brands to transform their digital presence and drive meaningful growth.
                </p>
                <Link
                  to="/about"
                  className="btn-ghost inline-flex items-center gap-2"
                  data-testid="about-cta"
                >
                  Learn More About Us
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollSection>

            <ScrollSection delay={0.2}>
              <div className="relative">
                <div className="gradient-border p-1">
                  <img
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                    alt="Focus Pixel Team"
                    className="rounded-xl w-full"
                  />
                </div>
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 glass-card p-6 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">200+</p>
                      <p className="text-fp-textMuted text-sm">Brands Served</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollSection>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-fp-surface/30">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Services</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Everything You Need to Grow
              </h2>
              <p className="text-fp-textMuted text-lg">
                Comprehensive digital solutions designed to elevate your brand and accelerate growth.
              </p>
            </div>
          </ScrollSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <ScrollSection key={service.title} delay={index * 0.1}>
                <div className="glass-card p-8 rounded-2xl card-hover group">
                  <div className="service-icon mb-6 group-hover:scale-110 transition-transform">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-fp-textMuted leading-relaxed mb-6">{service.description}</p>
                  <Link
                    to="/services"
                    className="text-primary text-sm font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </ScrollSection>
            ))}
          </div>

          <ScrollSection delay={0.4} className="text-center mt-12">
            <Link
              to="/services"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="services-cta"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollSection>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Why Choose Us</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                The Difference is in the Details
              </h2>
              <p className="text-fp-textMuted text-lg">
                We're not just another agency. Here's what sets us apart.
              </p>
            </div>
          </ScrollSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <ScrollSection key={item.title} delay={index * 0.1}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-fp-textMuted text-sm leading-relaxed">{item.description}</p>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="section-padding bg-fp-surface/30">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Our Work</p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                  Selected Projects
                </h2>
              </div>
              <Link
                to="/portfolio"
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="portfolio-cta"
              >
                View All Work
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Excellence Coaching', category: 'Digital Marketing', image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&q=80' },
              { title: 'Urban Fitness Studio', category: 'Brand Film', image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80' },
              { title: 'TechLearn Academy', category: 'Web Development', image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&q=80' },
            ].map((project, index) => (
              <ScrollSection key={project.title} delay={index * 0.1}>
                <Link to="/portfolio" className="group block">
                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-fp-bg via-fp-bg/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-primary text-sm font-medium mb-2">{project.category}</p>
                      <h3 className="text-xl font-semibold text-white">{project.title}</h3>
                    </div>
                  </div>
                </Link>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollSection>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-primary text-sm font-medium tracking-wider uppercase mb-4">Testimonials</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                What Our Clients Say
              </h2>
            </div>
          </ScrollSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <ScrollSection key={testimonial.author} delay={index * 0.1}>
                <div className="testimonial-card h-full flex flex-col">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-white/90 leading-relaxed mb-6 flex-grow">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-semibold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="text-white font-medium">{testimonial.author}</p>
                      <p className="text-fp-textMuted text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </ScrollSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <ScrollSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Accelerate Your Growth?
            </h2>
            <p className="text-fp-textMuted text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help you build a digital presence that drives 
              real business results. Book a free strategy call today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-call"
                className="btn-primary flex items-center justify-center gap-2 text-base"
                data-testid="final-cta-primary"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/get-proposal"
                className="btn-secondary flex items-center justify-center gap-2 text-base"
                data-testid="final-cta-secondary"
              >
                Get a Proposal
              </Link>
            </div>
          </ScrollSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
